import parse from 'html-react-parser';
import * as Immutable from 'immutable';
import moment from 'moment';
import * as React from 'react';
import { IntlShape } from 'react-intl';
import ReactTable, { Column, RowInfo, TableProps } from 'react-table';
import * as ReactTableHOCFixedColumns from 'react-table-hoc-fixed-columns';
import { Checkbox, Loader, Popup } from 'semantic-ui-react';
import 'react-table-hoc-fixed-columns/lib/styles.css';
import 'react-table/react-table.css';
import cc from 'classcat';

import { ENDPOINTS_COMPETITIONS } from '../../config';
import { AlphasAddToListContainer } from '../../containers/AlphasAddToList';
import { AlphaColumnBase } from '../../reducers/AlphasReducer/AlphasReducer.types';
import { AlphaTabType } from '../../shared/constants';
import { DatetimeFilters } from '../../shared/data';
import { initialSelectedColumns } from '../../shared/data/initialColumnsTable';
import { addModifiers, changeBigNumberFormat, changeSmallNumberFormat, handleDatePickerDateTime } from '../../shared/helpers';
import { formatMessageHelper } from '../../shared/helpers/internationalization';
import analytics from '../../shared/services/analytics/analytics';
import { AlphaCode } from '../AlphaCode';
import { CopyToClipboard } from '../../containers/DataExplorer/CopyToClipboard/CopyToClipboard';
import ReactDaterangepicker from '../ReactDaterangepicker/ReactDaterangepicker';
import '../Table/Table.scss';

import EditableOsmosisPointsCell from './EditableOsmosisPointsCell';
import { FilterInput } from './FilterInput';
import PopupHoverIntent from './PopupHoverIntent';
import { SelectFilters } from './SelectFilters';
import { ToolTip } from '../ToolTip';
import { FilterBlurEvent, TableFilterParams, TableFilterRendererProps } from './tableFilter.types';

interface TablePropsType extends TableProps {
  pages: number;
}

interface PropertyTypes {
  type: string;
  name: string;
  year: string;
}

const ReactTableFixedColumns = ReactTableHOCFixedColumns.default(ReactTable);

export const TableReact = (props: Partial<TablePropsType>) => {
  return <ReactTableFixedColumns {...props} />;
};

export default TableReact;

interface TableHeaderParams {
  isLeft?: boolean;
  name: string;
  sort: string | null;
  enableSorting?: boolean;
  order: boolean;
  className?: string;
  onClick: (id: string) => void;
  hint?: string;
}

export const TableHeader =
  ({ isLeft = true, name, sort, order, className, onClick, hint }: TableHeaderParams) =>
  (props: any) => {
    const id = props.column.columns !== undefined && props.column.columns.length > 0 ? props.column.columns[0].id : props.column.id;

    //tags are not sortable BRAINTECH-848
    //alphaId is not sortable BRAINTECH-5407
    if (sort !== null && id !== 'tags' && id !== 'alphaId') {
      const selected = id === sort;
      const handleClick = () => onClick(id);
      const modifier = selected ? (order ? 'table__sort--sort-asc' : 'table__sort--sort-desc') : undefined;

      return (
        <div className={cc([`table__sort table__sort--${id}`, modifier, className])} onClick={handleClick}>
          {hint ? (
            <div className="table__sort-label">
              {name}
              <ToolTip label={name} tooltipInfo={parse(hint)} on="hover" isTooltipInfoFormatted={true} isTooltipLabelFormatted={true} />
            </div>
          ) : (
            name
          )}
        </div>
      );
    } else {
      return <div className={isLeft ? 'table__left-content' : ''}>{name}</div>;
    }
  };

export const computeTableData = (
  data: any,
  editorWillMount: () => () => void,
  handleShowDetail: (alphaIndex: string) => void,
  columns: any
) =>
  data
    ? data
        .map((elem: any) => {
          const settings = elem.get('settings');
          const dateCreated: moment.Moment = moment(elem.get('dateCreated'));
          let properties: Immutable.List<PropertyTypes> = Immutable.List();
          if (elem.get('is')) {
            const details = elem.getIn(['is', 'details']);
            if (details) {
              properties = details.getIn(['schema', 'properties']);
            }
          }

          const dataColumns: any = {
            name: 'anonymous',
          };
          for (const column of columns) {
            const parent = elem.get(column.parent) || elem;
            const id = column.id;
            dataColumns[id] = parent.get(id) !== null && parent.get(id) !== undefined ? parent.get(id) : dataColumns[id];
          }

          const newEditor = editorWillMount();

          return {
            ...dataColumns,
            id: elem.get('id'),
            alphaId: elem.get('id'),
            color: elem.get('color'),
            favorite: elem.get('favorite'),
            hidden: elem.get('hidden'),
            dateCreated: dateCreated,
            settings,
            elem,
            properties,
            newEditor,
            handleShowDetail,
          };
        })
        .toJS()
    : [];

interface AddToListParameters {
  data: any;
  handleSetCurrentAlpha: any;
  intl: IntlShape;
  isSuper: boolean;
  /**
   * Optional GA tracker forwarded to AlphasAddToListContainer's onTrackOpen
   * prop. Kept generic so this Table component does not encode page-specific
   * GA event names — see BRAINTECH-5372 plan §"Shared Component
   * Disambiguation". Callers (e.g. AlphaTable.tsx) supply the page-specific
   * event.
   */
  onTrackOpen?: () => void;
}

function getIsCellDisabled(isSuper: boolean, row: any) {
  return isSuper && !row.original.elem.author;
}

export const addToList = ({ data, handleSetCurrentAlpha, intl, isSuper, onTrackOpen }: AddToListParameters) => ({
  Cell: (row: any) => {
    const setCurrentAlpha = () => handleSetCurrentAlpha(row.original.id);
    const isDisabled = getIsCellDisabled(isSuper, row);

    return (
      <AlphasAddToListContainer
        alphasData={data.alphas.length === 0 ? [row.original.id] : data.alphas}
        containerClasses="alpha-list-table__container--add-to-compare"
        btnClasses="alphas-list-table__clickable-icon add-to-compare"
        onAfterOpen={setCurrentAlpha}
        btnTitle={formatMessageHelper(intl, 'alphasAddToList.addAlphaToAList')}
        disabled={isDisabled}
        onTrackOpen={onTrackOpen}
      />
    );
  },
  maxWidth: 35,
  filterable: false,
});

export const favoriteIcon = (onClick: (id: string) => void, intl: IntlShape, isSuper: boolean) => ({
  Cell: (row: any) => {
    const isDisabled = getIsCellDisabled(isSuper, row);
    let icon;
    let labelTitleKey;
    if (row.original.favorite) {
      icon = 'star-selected';
      labelTitleKey = 'alphas.removeFromFavorites.title';
    } else {
      icon = 'star';
      labelTitleKey = 'alphas.addToFavorites.title';
    }

    const handleClick = (e: any) => {
      if (isDisabled) {
        return;
      }
      e.stopPropagation();
      onClick(row.original.id);
    };

    return (
      <div
        title={formatMessageHelper(intl, labelTitleKey)}
        onClick={handleClick}
        className={`alphas-list-table__clickable-icon ${addModifiers(icon, [isDisabled ? 'disabled' : ''])}`}
      />
    );
  },
  maxWidth: 35,
  filterable: false,
});

export const deleteAlphaIcon = (onClick: (id: string) => void, intl: IntlShape) => ({
  Cell: (row: any) => {
    const handleClick = () => onClick(row.original.id);

    return (
      <div
        onClick={handleClick}
        className="alphas-list-table__clickable-icon remove-from-compare"
        title={formatMessageHelper(intl, 'alphas.removeFromList.title')}
      />
    );
  },
  maxWidth: 35,
  filterable: false,
});

export const visibilityIcon = (onClick: (id: string) => void, intl: IntlShape) => ({
  Cell: (row: any) => {
    let icon;
    let labelTitle;
    if (row.original.hidden) {
      icon = 'invisible';
      labelTitle = 'alphas.unhideAlpha.title';
    } else {
      icon = 'visible';
      labelTitle = 'alphas.hideAlpha.title';
    }
    const handleClick = () => onClick(row.original.id);

    return (
      <div title={formatMessageHelper(intl, labelTitle)} onClick={handleClick} className={`alphas-list-table__clickable-icon ${icon}`} />
    );
  },
  maxWidth: 35,
  filterable: false,
});

interface CodeIconParams {
  settingsOptions: any;
  userId: string;
  isSuper: boolean;
}

export const codeIcon = ({ settingsOptions, userId, isSuper }: CodeIconParams) => ({
  Cell: (row: any) => {
    const author = row.original.elem.author;
    const isDisabled = getIsCellDisabled(isSuper, row);

    return (
      <Popup
        trigger={
          <div
            className={`alphas-list-table__clickable-icon ${addModifiers('code-btn', [isDisabled ? 'disabled' : ''])}`}
            onMouseEnter={() => {
              analytics.trackCustomEventOnce('alphas_hover_expression_check');
            }}
          />
        }
        content={
          <AlphaCode
            alpha={Immutable.fromJS(row.original.elem)}
            codeElem="alphas__code"
            editorWillMount={row.original.newEditor}
            hasTitle={false}
            modifiers={['table']}
            isPopupContent={true}
            settingsOptions={settingsOptions}
            userIdFromAlpha={author}
            disabled={isDisabled}
          />
        }
        basic={true}
        position="bottom left"
        disabled={isDisabled}
      />
    );
  },
  maxWidth: 35,
  filterable: false,
});

export const TableFilter =
  ({ tableOptions, column, filters, filtersUpdated, handleFilterBlur, choicesTransform, onFilterTrack }: TableFilterParams) =>
  (props: TableFilterRendererProps): React.ReactElement | null => {
    const id = column.id;
    const parent = column.parent;
    const columnType = column.type;
    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      props.onChange(event.target.value);
    };
    const onBlur = (event: FilterBlurEvent) => {
      if (
        !event.relatedTarget ||
        (event.relatedTarget && event.relatedTarget.className !== 'menu-top__item-btn menu-top__item-btn--link')
      ) {
        if (handleFilterBlur && filtersUpdated) {
          onFilterTrack?.(id);
          handleFilterBlur();
        } else {
          const val = !event.target ? event.value ?? '' : event.target.value;
          props.onChange(val);
        }
      }
    };
    if (!tableOptions) {
      return null;
    }

    const columnParent = (parent ? tableOptions[parent]?.children : undefined) ?? tableOptions;
    const resolvedOption = columnParent?.[id];
    const choices = resolvedOption?.choices;
    const effectiveType = resolvedOption?.type ?? columnType;
    if (choices) {
      const onSelectChange = (data: string) => {
        onFilterTrack?.(id);
        props.onChange(data);
      };
      return (
        <SelectFilters choices={choices} filter={props.filter} onChange={onSelectChange} id={id} choicesTransform={choicesTransform} />
      );
    } else if (effectiveType === 'datetime') {
      const datetimeID = parent ? `${parent}.${id}` : id;
      const onDateChange = (dates: moment.Moment[] | string[]) => {
        onFilterTrack?.(id);
        // handleDatePickerDateTime converts the picked moments to NY-tz ISO strings, so the
        // committed filter value is always a string[] here.
        props.onChange(dates as string[]);
      };

      return (
        <ReactDaterangepicker
          handleDatesBeforeChange={handleDatePickerDateTime}
          handleChange={onDateChange}
          maxYear={parseInt(moment().format('YYYY'), 10)}
          minYear={parseInt(moment().subtract(10, 'years').format('YYYY'), 10)}
          ranges={true}
          showDropdowns={true}
          startDate={filters[`${datetimeID}>`]}
          endDate={filters[`${datetimeID}<`]}
          placeholder="Search"
          modifiers={['alpha-table']}
        />
      );
    } else {
      const value = props.filter ? props.filter.value : '';

      return <FilterInput filters={filters} id={id} parent={parent} type={columnType} value={value} onChange={onChange} onBlur={onBlur} />;
    }
  };

export const GeneralFilter =
  (handleBulkCheckbox: (data: any) => void, intl: IntlShape, allChecked: boolean, isLoading = false) =>
  () =>
    (
      <div className="alphas-list-table__general">
        {isLoading && (
          <div className="alphas-list-table__general-loader">
            <Loader className="alphas-list-loader" active={true} inline="centered" size="small" />
          </div>
        )}
        <div
          className="alphas-list-table__general-filter"
          title={formatMessageHelper(intl, allChecked ? 'alphas.unselectAlphas.title' : 'alphas.selectAlphas.title')}
        >
          <Checkbox checked={allChecked} onChange={handleBulkCheckbox} />
        </div>
      </div>
    );

const getNumberFormatType = (id: string, properties: Immutable.List<Immutable.Map<string, string>>, typeColumn?: string) => {
  if (id && properties.size > 0) {
    const type: any = properties.filter((property: Immutable.Map<string, string>) => property.get('name') === id);

    if (type.size > 0) {
      return type.first().get('type');
    } else if (typeof typeColumn === 'string') {
      return typeColumn;
    } else {
      return 'none';
    }
  } else if (typeof typeColumn === 'string') {
    return typeColumn;
  } else {
    return 'none';
  }
};

export const TableCell = (options: any, onSaveOsmosisPoints?: (alphaId: string, value: number | null) => Promise<any>) => (row: any) => {
  if (row.column.id === 'osmosisPoints' && onSaveOsmosisPoints) {
    return <EditableOsmosisPointsCell value={row.value} alphaId={row.original.id} onSave={onSaveOsmosisPoints} row={row} />;
  }

  const choicesOptions: any = options;
  const choices: any = choicesOptions ? choicesOptions.get(row.column.id) : undefined;
  const onContentClick = () => row.original.handleShowDetail(row.original.id);
  const modifiers = typeof row.value === 'number' ? ['number'] : ['text'];
  const stageClass =
    row.column.id === 'status'
      ? `alphas-list-table__cell-content-status alphas-list-table__cell-content-status--${row.value?.toString().toLowerCase()}`
      : '';
  modifiers.push(row.column.id);
  let value = row.value;
  const filteredColumn = initialSelectedColumns(AlphaTabType.SUPER_SELECTION).find(
    (alphaColumn: AlphaColumnBase) => alphaColumn.id === row.column.id
  );
  if (row.column.id === 'alphaId') {
    return (
      <div className={addModifiers('alphas-list-table__cell-content', modifiers)} onClick={onContentClick}>
        <span className="alpha-id-cell__value">{row.value}</span>
        <CopyToClipboard
          value={row.value}
          className="copy-to-clipboard--alpha-id"
          onCopy={() => analytics.trackCustomEvent('alphas_click_copy_id')}
        />
      </div>
    );
  } else if (row.column.id === 'tags') {
    return <PopupHoverIntent modifiers={modifiers} onContentClick={onContentClick} stageClass={stageClass} row={row} />;
  } else if (row.column.id === 'competitions') {
    return (
      <div className={addModifiers('alphas-list-table__cell-content', modifiers)} onClick={onContentClick}>
        <div className={stageClass}>
          {' '}
          {row?.value?.map((competition: any) => (
            <img
              className="alphas-competition__icon"
              key={competition.id}
              title={competition.name}
              src={`${ENDPOINTS_COMPETITIONS.competitions}/${competition.id}/icon`}
            />
          ))}{' '}
        </div>
      </div>
    );
  } else if (typeof row.value === 'number') {
    if (row.column.id === 'actualWeight') {
      value = changeSmallNumberFormat({
        value: row.value,
        type: getNumberFormatType(row.column.id, row.original.properties),
      });
    } else if (row.column.id === 'bookSize') {
      value = changeBigNumberFormat(row.value, 0);
    } else if (row.column.id === 'correlation') {
      value = changeSmallNumberFormat({ value: row.value, type: 'none' });
    } else if (filteredColumn && filteredColumn.type !== 'integer' && filteredColumn.type !== 'choices') {
      value =
        row.value < 10 && row.value > -10
          ? changeSmallNumberFormat({
              value: row.value,
              type: getNumberFormatType(row.column.id, row.original.properties, filteredColumn && filteredColumn.type),
            })
          : changeBigNumberFormat(row.value, 2);
    } else {
      value = row.value;
    }
  } else if (choices) {
    const newValue: any = choices.filter((choice: any) => choice.get('value') === value);
    value = newValue.size === 1 ? newValue.first().get('text') : value;
  } else {
    value = DatetimeFilters.includes(row.column.id) && row.value ? moment(row.value).tz('America/New_York').format('L z') : row.value;
  }

  return (
    <div className={addModifiers('alphas-list-table__cell-content', modifiers)} onClick={onContentClick}>
      <div className={stageClass}> {value} </div>
    </div>
  );
};

interface SingleCheckboxCellParams {
  handleSingleCheckbox: (data: string) => void;
  intl: IntlShape;
  bulkAlphas: string[];
  isSuper: boolean;
}

export const SingleCheckboxCell =
  ({ handleSingleCheckbox, intl, bulkAlphas, isSuper }: SingleCheckboxCellParams) =>
  (row: any) => {
    const onContentClick = () => handleSingleCheckbox(row.original.id);
    const checked = bulkAlphas.indexOf(row.original.id) !== -1;
    const isDisabled = getIsCellDisabled(isSuper, row);

    return (
      <div
        className="alphas-list-table__clickable-icon checkbox"
        title={formatMessageHelper(intl, checked ? 'alphas.unselectAlpha.title' : 'alphas.selectAlpha.title')}
      >
        <Checkbox checked={checked} onChange={onContentClick} disabled={isDisabled} />
      </div>
    );
  };

type GetTrProps = NonNullable<TableProps['getTrProps']>;

export const composeGetTrProps = (...getTrPropsFns: GetTrProps[]) => {
  return (...args: Parameters<GetTrProps>) => {
    // @ts-expect-error
    return getTrPropsFns.reduce((props, fn) => ({ ...props, ...fn(...args) }), {});
  };
};

export const RowHighlightFocus = (alphaDetailId: string | null) => {
  return (_: unknown, rowInfo?: RowInfo) => {
    return rowInfo?.original.id === alphaDetailId ? { className: '-focus' } : {};
  };
};

export const RowColor: GetTrProps = (_: unknown, rowInfo?: RowInfo, __?: Column) => {
  const rowColors: any = {
    yellow: '#fcf75f',
    purple: '#b77ac8',
    green: '#8fbe56',
    red: '#e05153',
    blue: '#6a9fd5',
  };

  // $off-yellow-50: #fcf656;
  // $reddish-50: #dd494a;
  // $leaf-50: #87b84d;
  // $dark-sky-blue-50: #6099d1;
  // $warm-purple-50: #b171c2;

  return rowInfo && rowInfo.original?.color ? { style: { background: rowColors[rowInfo.original.color.toLowerCase()] } } : {};
};
